import {Injectable, OnDestroy} from '@angular/core';
import {SyncStatusItemsTable} from '../../tables/sync-status-items.table';
import {SyncStatusItem} from '../../interfaces/core/sync-status-item';
import {TableSyncSettings} from '../../interfaces/core/table-sync-settings';
import {CdRaisTable} from '../../tables/care/cd-rais.table';
import {CdMdsAnswersTable} from '../../tables/care/cd-mds-answers.table';
import {WmWoundDocumentationEntriesTable} from '../../tables/wound-management/wm-wound-documentation-entries.table';
import {SyncItem} from '../../interfaces/core/sync-item';
import {WmWoundAnswersTable} from '../../tables/wound-management/wm-wound-answers.table';
import {db} from '../../tables/db.service';
import {WmWoundImagesTable} from '../../tables/wound-management/wm-wound-images.table';
import {WmWoundDocumentationsTable} from '../../tables/wound-management/wm-wound-documentations.table';
import {environment} from '../../../environments/environment';
import {CdAimEvaluationsTable} from '../../tables/care/cd-aim-evaluations.table';
import {AuthService} from './auth.service';
import {SyncException} from '../../exceptions/sync.exception';
import {RequestCancelledError} from '../../exceptions/request-cancelled.error';
import {ErrorLogFactory} from '../../factories/core/error-log-factory';
import {Subscription} from 'rxjs';
import {ConnectionService} from './connection.service';
import {ConflictHandler} from '../../tables/conflict-handling/conflict-handler';
import {ErrorLogsTable} from '../../tables/error-logs/error-logs.table';
import {TtTravelEntriesTable} from '../../tables/time-tracking/tt-travel-entries.table';
import {TtActivityEntriesTable} from '../../tables/time-tracking/tt-activity-entries.table';
import {TtProductEntriesTable} from '../../tables/time-tracking/tt-product-entries.table';
import {CustomerDocumentsTable} from '../../tables/customer-documents/customer-documents.table';
import {ProdRequestsTable} from '../../tables/product-management/prod-requests.table';
import {HttpService} from './http.service';
import {VitalSignEntriesTable} from '../../tables/vital-signs/vital-sign-entries.table';
import {PrProcessReportsTable} from '../../tables/process-reports/pr-process-reports.table';
import {TtLiveActivityEntriesTable} from '../../tables/time-tracking/tt-live-activity-entries.table';
import {CdExecutionProofsTable} from '../../tables/care/cd-execution-proofs.table';
import {CdTemporaryMeasuresTable} from '../../tables/care/cd-temporary-measures.table';
import {
  CdoCustomerRequirementsTable
} from '../../tables/customer-documents/cdo-customer-requirements.table';
import {CdoDocumentRequestsTable} from '../../tables/customer-documents/cdo-document-requests.table';
import {TableName} from '../../enums/core/table-name';

@Injectable({
  providedIn: 'root'
})
export class SyncService implements OnDestroy {

  private static readonly SYNCING_WAIT_INTERVAL: number = 500;
  private static readonly MAX_SYNCING_WAIT_ATTEMPTS: number = 30;

  private _tableSyncSettings: {
    [key: string]: TableSyncSettings
  };

  private _syncStatusItems: SyncStatusItem[] = [];
  private _syncing: boolean = false;
  private _isSynced: boolean = false;
  private _interval: any = null;
  private _isConnected: boolean = false;
  private _lockSyncing: boolean = false;

  private $_isConnected: Subscription;
  private $_syncStatusItemsChanges: Subscription;

  constructor(private syncStatusItemsTable: SyncStatusItemsTable,
              private connectionService: ConnectionService,
              private cdRaisTable: CdRaisTable,
              private httpService: HttpService,
              private cdAimEvaluationsTable: CdAimEvaluationsTable,
              private cdMdsAnswersTable: CdMdsAnswersTable,
              private conflictHandler: ConflictHandler,
              private errorLogsTable: ErrorLogsTable,
              private vitalSignEntriesTable: VitalSignEntriesTable,
              private wmWoundImagesTable: WmWoundImagesTable,
              private wmWoundDocumentationsTable: WmWoundDocumentationsTable,
              private wmWoundDocumentationEntriesTable: WmWoundDocumentationEntriesTable,
              private wmWoundAnswersTable: WmWoundAnswersTable,
              private ttActivityEntriesTable: TtActivityEntriesTable,
              private ttProductEntriesTable: TtProductEntriesTable,
              private customerDocumentsTable: CustomerDocumentsTable,
              private ttTravelEntriesTable: TtTravelEntriesTable,
              private processReportsTable: PrProcessReportsTable,
              private ttLiveActivityEntriesTable: TtLiveActivityEntriesTable,
              private cdExecutionProofsTable: CdExecutionProofsTable,
              private cdTemporaryMeasuresTable: CdTemporaryMeasuresTable,
              private cdoCustomerRequirementsTable: CdoCustomerRequirementsTable,
              private cdoDocumentRequestsTable: CdoDocumentRequestsTable,
              private prodRequestsTable: ProdRequestsTable) {
  }

  get syncing(): boolean {
    return this._syncing;
  }

  get isSynced(): boolean {
    return this._isSynced;
  }

  /**
   *
   */
  public ngOnDestroy(): void {
    if(this._interval) {
      clearInterval(this._interval);
    }
    this.$_syncStatusItemsChanges?.unsubscribe();
    this.$_isConnected?.unsubscribe();
  }

  /**
   * This function checks directly in the database if the sync is done
   */
  public async isSyncedSecurely(): Promise<boolean> {
    this._isSynced = await this.syncStatusItemsTable.isSynced();
    return this._isSynced;
  }

  /**
   *
   */
  public lockSyncing(): void {
    this._lockSyncing = true;
  }

  /**
   *
   */
  public unlockSyncing(forceSync: boolean = false): void {
    this._lockSyncing = false;
    this.processSyncing(forceSync).finally();
  }

  /**
   *
   */
  public async init(): Promise<void> {
    await this.ttLiveActivityEntriesTable.removeDuplicates();

    this._tableSyncSettings = {
      cdRais: this.cdRaisTable.tableSyncSettings,
      cdMdsAnswers: this.cdMdsAnswersTable.tableSyncSettings,
      cdAimEvaluations: this.cdAimEvaluationsTable.tableSyncSettings,
      wmWoundDocumentationEntries: this.wmWoundDocumentationEntriesTable.tableSyncSettings,
      wmWoundAnswers: this.wmWoundAnswersTable.tableSyncSettings,
      wmWoundImages: this.wmWoundImagesTable.tableSyncSettings,
      wmWoundDocumentations: this.wmWoundDocumentationsTable.tableSyncSettings,
      errorLogs: this.errorLogsTable.tableSyncSettings,
      ttTravelEntries: this.ttTravelEntriesTable.tableSyncSettings,
      ttActivityEntries: this.ttActivityEntriesTable.tableSyncSettings,
      ttProductEntries: this.ttProductEntriesTable.tableSyncSettings,
      customerDocuments: this.customerDocumentsTable.tableSyncSettings,
      // The server resolves conflicts last write wins, so no conflict handler
      cdoCustomerRequirements: this.cdoCustomerRequirementsTable.tableSyncSettings,
      cdoDocumentRequests: this.cdoDocumentRequestsTable.tableSyncSettings,
      prodRequests: this.prodRequestsTable.tableSyncSettings,
      vitalSignEntries: this.vitalSignEntriesTable.tableSyncSettings,
      ttLiveActivityEntries: this.ttLiveActivityEntriesTable.tableSyncSettings,
      cdExecutionProofs: this.cdExecutionProofsTable.tableSyncSettings,
      prProcessReports: this.processReportsTable.tableSyncSettings,
      cdTemporaryMeasures: this.cdTemporaryMeasuresTable.tableSyncSettings
    };

    await this.repairInterruptedSync();

    this._isSynced = await this.syncStatusItemsTable.isSynced();

    // Waiting until init is finished | login trigger clear many times
    setTimeout(() => {
      this.initSyncInterval();

      this.$_isConnected = this.connectionService.isConnected$.subscribe((_isConnected) => {
        this._isConnected = _isConnected;
      });
    }, 500);
  }

  /**
   * Repairs sync processes that were interrupted before they could be completed.
   *
   * A running synchronisation is locked and awaited first, because a claimed queue entry of a running upload must not
   * be released. Only tables with `syncMode: 'default'` are handled, so the `many` and `all` semantics stay unchanged.
   */
  public async repairInterruptedSync(): Promise<void> {
    if(!this._tableSyncSettings) {
      return;
    }

    const wasSyncingLocked = this._lockSyncing;
    this._lockSyncing = true;

    try {
      if(!await this.waitForSyncingFinished()) {
        this.infoLog('Recovery skipped: Synchronization still running.');
        return;
      }

      const tableNames =
        Object.keys(this._tableSyncSettings)
          .filter(tableName => this._tableSyncSettings[tableName].syncMode === 'default') as TableName[];

      await this.syncStatusItemsTable.repairInterruptedSyncStatusItems(tableNames);
      await this.syncStatusItemsTable.repairResurrectedTombstones(tableNames);
    }
    finally {
      this._lockSyncing = wasSyncingLocked;
    }
  }

  /**
   * Waits until a running synchronisation has finished.
   *
   * @returns false if the synchronisation is still running after the maximum waiting time
   * @private
   */
  private async waitForSyncingFinished(): Promise<boolean> {
    let attempts = 0;
    while(this._syncing && attempts < SyncService.MAX_SYNCING_WAIT_ATTEMPTS) {
      await this.sleep(SyncService.SYNCING_WAIT_INTERVAL);
      attempts++;
    }
    return !this._syncing;
  }

  /**
   *
   * @private
   */
  private initSyncInterval(): void {
    if(this._interval) {
      clearInterval(this._interval);
    }
    this._interval = setInterval(() => {
      this.processSyncing().finally();
    }, environment.syncInterval);
  }

  /**
   *
   * @param force
   * @private
   */
  public async processSyncing(force: boolean = false): Promise<void> {
    try {
      // Zu synchronisierende Items holen
      this._syncStatusItems = await this.syncStatusItemsTable.getToSync();
      this._isSynced = this._syncStatusItems.length === 0;
    }
    catch(e) {
      console.error('Storage error:', e);
      this._isSynced = false;
      return;
    }

    // Falls manuell forciert wurde, laufende Requests abbrechen und das Ende der laufenden Loop abwarten,
    // damit keine zweite Loop parallel startet
    if (force) {
      this.httpService.cancelRequests();
      this.initSyncInterval();
      if (!await this.waitForSyncingFinished()) {
        this.infoLog('Force sync skipped: previous run still active.');
        return;
      }
      this._syncStatusItems = await this.syncStatusItemsTable.getToSync();
      this._isSynced = this._syncStatusItems.length === 0;
    }

    // Vorzeitiger Abbruch, wenn gerade gesynct oder blockiert
    if(this._syncing || this._lockSyncing) {
      return;
    }

    // Authentifizierung prüfen
    if (!AuthService.isAuthenticated()) {
      this.infoLog('Synchronization: Not authenticated');
      return;
    }

    // Verbindung prüfen
    if (!this._isConnected) {
      this.infoLog('Synchronization: No connection');
      return;
    }

    // Jetzt ist klar: Wir dürfen syncen → Status setzen
    this._syncing = true;

    try {
      if(this._isSynced) {
        this.infoLog('Nothing to sync.');
        return;
      }
      else {
        this.infoLog('Synchronization started...');
      }

      const rejectedExecutionProofIds = new Set<number>();
      while(this._syncStatusItems.length > 0) {
        const currentItem = this._syncStatusItems[0];
        try {
          await this.processSyncStatusItem(currentItem);
        }
        catch (error) {
          // A rejected DNW remains pending for correction/retry, but must not prevent
          // independent proofs from uploading. Temporary measures and transport errors
          // still stop the run, preserving the ordering of new measures before proofs.
          if (currentItem.tableName !== TableName.CdExecutionProofs
            || !(error instanceof SyncException)
            || ![409, 422].includes(error.code)) {
            throw error;
          }
          rejectedExecutionProofIds.add(currentItem.id);
        }
        await this.sleep(200);
        this._syncStatusItems = (await this.syncStatusItemsTable.getToSync())
          .filter(item => !rejectedExecutionProofIds.has(item.id));
      }

      this.infoLog(rejectedExecutionProofIds.size > 0
        ? 'Synchronization finished with pending execution-proof errors.'
        : 'Synchronization completed.');
      await this.sleep(2000);
    }
    catch (syncException) {
      this._isSynced = false;

      // Ein Abbruch ist kein Fehler: kein Error-Sleep, damit ein Force-Sync sofort neu starten kann
      if (syncException instanceof RequestCancelledError) {
        this.infoLog('Synchronization cancelled.');
        return;
      }

      if (syncException instanceof SyncException) {
        console.error('[SyncService] SyncException:', syncException.message, syncException.code);
      }
      else if (!environment.production) {
        console.error('[SyncService] Unexpected sync error:', syncException);
      }

      this.infoLog('Synchronization failed.');
      await this.sleep(1000 * 10);
    }
    finally {
      this._syncing = false;
    }
  }

  /**
   *
   * @param syncStatusItem
   * @private
   */
  private async processSyncStatusItem(syncStatusItem: SyncStatusItem): Promise<void> {
    const tableSyncSetting = this._tableSyncSettings[syncStatusItem.tableName];
    if(!tableSyncSetting) {
      throw new SyncException('No sync settings for table: ' + syncStatusItem.tableName);
    }

    try {
      if(tableSyncSetting.syncMode === 'many') {
        await this.handleSyncMany(tableSyncSetting, syncStatusItem);
      }
      else if(tableSyncSetting.syncMode === 'all') {
        await this.handleSyncAll(tableSyncSetting, syncStatusItem);
      }
      else if(tableSyncSetting.syncMode === 'default') {
        if(syncStatusItem.deleted === 1) {
          await this.handleDeletion(tableSyncSetting, syncStatusItem);
        }
        else {
          await this.handleSync(tableSyncSetting, syncStatusItem);
        }
      }
    } catch(e: any) {
      throw e;
    }
  }

  /**
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleDeletion(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    try {
      if(syncStatusItem.deleted === 1) {
        await tableSyncSetting.adapter.delete(syncStatusItem);
        await this.syncStatusItemsTable.setSyncDeletedItems([syncStatusItem]);
      }
    } catch(error) {
      // Ein Abbruch ist kein Fehler; es wurde kein Claim gesetzt, daher nur weiterwerfen
      if(error instanceof RequestCancelledError) {
        throw error;
      }
      // Der Datensatz existiert am Server nicht (mehr), die Löschung gilt als abgeschlossen
      if(error instanceof SyncException && error.code === 404) {
        await this.syncStatusItemsTable.setSyncDeletedItems([syncStatusItem]);
        return;
      }
      const syncException = this.toSyncException(error);
      await this.syncStatusItemsTable.setSyncError([syncStatusItem.id], syncException);
      throw syncException;
    }
  }

  /**
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleSync(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    // The queue entry is claimed before the local item is read, so a deletion in between cannot be overwritten
    const isClaimed = await this.syncStatusItemsTable.claimForSync(syncStatusItem.id);
    if(!isClaimed) {
      // The entry was removed or became a tombstone, so the reloaded queue handles it
      return;
    }

    const table = db.getSyncTableByTableName(syncStatusItem.tableName);
    const itemToSync = await table.where('uuid').equals(syncStatusItem.primaryKey).first();

    if(!itemToSync) {
      const currentSyncStatusItem = await this.syncStatusItemsTable.getByPrimaryKey(syncStatusItem.primaryKey);

      // The item was deleted regularly after the claim, so the reloaded queue sends the deletion
      if(!currentSyncStatusItem || currentSyncStatusItem.deleted === 1) {
        await this.syncStatusItemsTable.releaseSyncClaim(syncStatusItem.id);
        return;
      }

      // The local item is gone without a tombstone (e.g. after a dropped table in a DB migration). The entry would
      // block the queue forever, so it is resolved deterministically: entries without server contact are removed,
      // entries with server contact become a tombstone so the server record is deleted too.
      const hadServerContact = !!currentSyncStatusItem.lastSync;
      await this.syncStatusItemsTable.resolveOrphanedEntry(syncStatusItem.id, hadServerContact);
      await this.logOrphanedEntry(currentSyncStatusItem, hadServerContact);
      return;
    }

    try {
      await tableSyncSetting.adapter.save(itemToSync);
      await this.syncStatusItemsTable.setSyncSuccessfully([itemToSync], syncStatusItem.tableName);
    } catch(error) {
      // Ein Abbruch ist kein Fehler: nur den Claim freigeben, kein Fehlerstatus
      if(error instanceof RequestCancelledError) {
        await this.syncStatusItemsTable.releaseSyncClaim(syncStatusItem.id);
        throw error;
      }
      const syncException = this.toSyncException(error);
      await this.syncStatusItemsTable.setSyncError([syncStatusItem.id], syncException);
      if(error instanceof SyncException) {
        await this.conflictHandler.handleConflict(syncStatusItem, syncException);
      }
      throw syncException;
    }
  }

  /**
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleSyncAll(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    const table = db.getSyncTableByTableName(syncStatusItem.tableName);
    const itemToSync = await table.where('uuid').equals(syncStatusItem.primaryKey).first();
    // The adapter's foreign key belongs to the concrete item type of its table
    const foreignKey = tableSyncSetting.adapter.foreignKey as keyof SyncItem;
    let itemsToSync: SyncItem[];

    if(foreignKey) {
      const foreignKeyValue = itemToSync[foreignKey] as any;
      itemsToSync = await table.where(foreignKey).equals(foreignKeyValue).toArray();
    }
    else {
      itemsToSync = await table.toArray();
    }

    if(itemsToSync.length <= 0) {
      throw new Error('Dependent items not found');
    }

    const syncStatusItems =
      await this.syncStatusItemsTable.getAllByPrimaryKey(itemsToSync.map(itemToSync => itemToSync.uuid));
    const syncStatusItemsIds = syncStatusItems.map(_syncStatusItem => _syncStatusItem.id);

    try {
      await this.syncStatusItemsTable.setSyncInProcess(syncStatusItemsIds);
      await tableSyncSetting.adapter.saveMany(itemsToSync);
      await this.syncStatusItemsTable.setSyncSuccessfully(itemsToSync, syncStatusItem.tableName);
    } catch(error) {
      // Ein Abbruch ist kein Fehler: nur die Claims freigeben, kein Fehlerstatus
      if(error instanceof RequestCancelledError) {
        for(const syncStatusItemId of syncStatusItemsIds) {
          await this.syncStatusItemsTable.releaseSyncClaim(syncStatusItemId);
        }
        throw error;
      }
      const syncException = this.toSyncException(error);
      await this.syncStatusItemsTable.setSyncError(syncStatusItemsIds, syncException);
      throw syncException;
    }
  }

  /**
   * TODO: Deletion is not handled yet
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleSyncMany(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    await this.sleep(1000);

    const table = db.getSyncTableByTableName(syncStatusItem.tableName);
    const syncStatusItems: SyncStatusItem[] = [];
    for(const _syncStatusItem of this._syncStatusItems) {
      if(_syncStatusItem.tableName === syncStatusItem.tableName) {
        syncStatusItems.push(_syncStatusItem);
      }
      else {
        break;
      }
    }

    let itemsToSync =
      await table.where('uuid').anyOf(
        syncStatusItems.map(_syncStatusItem => _syncStatusItem.primaryKey)
      ).toArray();

    if(itemsToSync.length <= 0) {
      throw new Error('Dependent items not found');
    }

    // The adapter's foreign key belongs to the concrete item type of its table
    const foreignKey = tableSyncSetting.adapter.foreignKey as keyof SyncItem;
    if(itemsToSync.length > 0 && foreignKey) {
      const firstItemToSync = itemsToSync[0];
      const foreignKeyValue = firstItemToSync[foreignKey];
      itemsToSync = itemsToSync.filter(_itemToSync => _itemToSync[foreignKey] === foreignKeyValue);
    }

    try {
      await tableSyncSetting.adapter.saveMany(itemsToSync);
      await this.syncStatusItemsTable.setSyncSuccessfully(itemsToSync, syncStatusItem.tableName);
    } catch(error) {
      // Ein Abbruch ist kein Fehler; es wurde kein Claim gesetzt, daher nur weiterwerfen
      if(error instanceof RequestCancelledError) {
        throw error;
      }
      const syncException = this.toSyncException(error);
      const syncStatusItems =
        await this.syncStatusItemsTable.getAllByPrimaryKey(itemsToSync.map(itemToSync => itemToSync.uuid));
      await this.syncStatusItemsTable.setSyncError(syncStatusItems.map(syncStatusItem => syncStatusItem.id), syncException);
      if(error instanceof SyncException) {
        await this.conflictHandler.handleConflict(syncStatusItem, syncException);
      }
      throw syncException;
    }
  }

  /**
   * Wraps unexpected errors into a SyncException, so every failure sets a visible error state on the queue entry
   * instead of leaving it as "pending" without a reason.
   *
   * @param error
   * @private
   */
  private toSyncException(error: any): SyncException {
    if(error instanceof SyncException) {
      return error;
    }
    return new SyncException(error?.message ?? 'Unknown sync error', 500);
  }

  /**
   * Keeps a resolved orphaned queue entry visible by writing it to the synced error logs.
   *
   * @param syncStatusItem
   * @param hadServerContact
   * @private
   */
  private async logOrphanedEntry(syncStatusItem: SyncStatusItem, hadServerContact: boolean): Promise<void> {
    try {
      const errorLog = ErrorLogFactory.fromError(new Error(
        'Resolved orphaned sync status item: ' + syncStatusItem.tableName + '/' + syncStatusItem.primaryKey +
        (hadServerContact ? ' (converted to tombstone)' : ' (removed, never synced)')
      ));
      await this.errorLogsTable.add(errorLog);
    }
    catch(error) {
      console.warn('Could not log orphaned sync status item:', error);
    }
  }

  /**
   *
   * @param milliseconds
   * @private
   */
  private async sleep(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        resolve();
      }, milliseconds);
    });
  }

  /**
   *
   * @param info
   * @private
   */
  private infoLog(info: string): void {
    if(!environment.production) {
      console.log(info);
    }
  }

}
